import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import { actions, ProjectService, calculateServiceCost, getServiceName } from 'entities';

import { Alert, LabeledTextarea, Column, Row, Link, Label } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { EarthIcon } from 'components/Icons';
import { AttachedFiles } from 'components/Uploads';
import { MonetaryInput, VariableCostInput } from 'components/Inputs';

import { FullyInvoicedRepriceAlert } from 'pages/Invoice/components';
import { ServiceOutsourcedTag } from 'pages/Projects/components';

import { CreatedBy } from '../components';
import { InvoiceRequestedBy } from './InvoiceRequestedBy';
import { PriceChange } from './PriceChange';
import { RepriceReason } from './RepriceReason';
import { ConfirmRepriceButton } from './ConfirmButton';

import { useToast } from 'providers/Toast';
import { useLang } from 'hooks/lang';

import { MESSAGES } from '../utils';
import { ProjectWithRepriceRequestParse } from '.';
import {
  RepriceService,
  extractOnlyChangedServices,
  unwrapServicesToAPIFormat,
  updateServicesToRenderFormat,
  verifyIfAnyValueWasChanged,
  verifyFilling,
} from './utils';
import { omitUndefined } from 'utils/object';
import { getCountryLabel } from 'utils/countries';

interface SingleRepriceProps {
  project: ProjectWithRepriceRequestParse;
  removeSelf: () => void;
}

export function SingleReprice({ project, removeSelf }: SingleRepriceProps): JSX.Element {
  const { addToast } = useToast();
  const { t } = useLang();

  const {
    name,
    requestData: { comment, id: repriceId, invoice, origin, requestedBy, reason, files },
    services,
    clientName,
    id,
    owner,
    createdBy,
    tasks,
    currentServices,
  } = project;

  const fromInvoice = origin === 'INVOICE';

  // this is an anti-pattern, but we will come back to fix it later
  const [control, setControl] = useState(unwrapServicesToAPIFormat(services));

  const commentRef = useRef<HTMLTextAreaElement>(null);

  // revise this format later, as it seems pretty bad to have to do this
  // either build a component tree to make this easy or change the format all together
  const changeValues = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = target;

      const [country, id, prop, index] = name.split('#');

      setControl((old) =>
        old.map((singleService) => {
          if (singleService.id !== id) return singleService;

          const { costs, service, id: serviceId, name, outsourceConfig, ...rest } = singleService;

          const updatedCosts = {
            ...costs,
            [country]: costs[country].map((cost, costIndex) =>
              costIndex === Number(index) ? { ...cost, [prop]: value } : cost,
            ),
          };

          const currentService = currentServices.find(
            (projectService) => projectService.id === id,
          ) as ProjectService;

          return omitUndefined({
            id: serviceId,
            service,
            name,
            costs: updatedCosts,

            ...rest,

            outsourceConfig: outsourceConfig
              ? {
                  ...outsourceConfig,

                  clientCost: {
                    ...outsourceConfig?.clientCost,

                    value: calculateServiceCost({
                      ...currentService,

                      costs: {
                        ...currentService.costs,
                        ...updatedCosts,
                      },
                    }),
                  },
                }
              : undefined,
          });
        }),
      );
    },
    [currentServices],
  );

  const handleProviderCostChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = target;

      const [, id] = name.split('#');

      setControl((old) =>
        old.map((service) =>
          service.id === id
            ? ({
                ...service,

                outsourceConfig: {
                  ...service.outsourceConfig,

                  providerCost: {
                    ...service.outsourceConfig?.providerCost,
                    value,
                  },

                  clientCost: {
                    ...service.outsourceConfig?.clientCost,

                    value: calculateServiceCost({
                      ...(currentServices.find(
                        (projectService) => projectService.id === id,
                      ) as ProjectService),

                      ...service,
                    }),
                  },
                },
              } as RepriceService)
            : service,
        ),
      );
    },
    [currentServices],
  );

  const updatedServices = updateServicesToRenderFormat(control, services);

  const canSubmit = verifyFilling(control);

  const answerReprice = actions.projects.answerProjectReprice.use({
    onSuccess: removeSelf,
  });

  const updatePrices = async () => {
    const hasChanges = verifyIfAnyValueWasChanged(control, services);

    const comment = commentRef.current?.value.trim() || '';

    if (!hasChanges && fromInvoice) return addToast(MESSAGES.invoiceRepriceRequiresChanges(t));
    if (!hasChanges && !comment) return addToast(MESSAGES.repriceCommentOnNoChange(t));

    await answerReprice.execute({
      repriceId,
      project: id,
      comment,
      repricedServices: fromInvoice
        ? extractOnlyChangedServices(updatedServices, currentServices)
        : updatedServices,
    });
  };

  return (
    <Column className="rounded-lg border p-5 shadow-lg" gap="6">
      <Row justifyContent="space-between">
        <Row gap="2" fullWidth={false}>
          <Link
            className="text-lg font-semibold text-primary"
            to="projectProfile"
            params={{ projectId: id }}
          >
            {name}
          </Link>

          <CreatedBy id={createdBy} />
        </Row>

        <UserAvatar user={owner} name nameClassName="font-medium" nameGap="4" />
      </Row>

      {fromInvoice && (
        <Alert level="info" title={t('reprice-from-invoice-team')}>
          <Column>
            {t('invoice-team-reprice-description')}

            <InvoiceRequestedBy userId={requestedBy as string} />
          </Column>
        </Alert>
      )}

      <p className="-mt-2 font-medium">{clientName}</p>

      <RepriceReason reason={reason} />

      <Column>
        {updatedServices.map(({ id, costs, outsourceConfig }) => (
          <Column key={`reprice-${project.id}--${id}`}>
            <Row
              gap="2"
              flexWrap="wrap"
              className="rounded-lg bg-primary-dark p-2 font-medium text-primary-foreground"
            >
              <span>{getServiceName(id, tasks)}</span>

              {!!outsourceConfig && <ServiceOutsourcedTag />}
            </Row>

            {costs.map(({ costs, country, justification }) => (
              <Column className="px-2" gap="6" key={`reprice-${project.id}--${id}--${country}`}>
                <Row gap="2">
                  <EarthIcon />

                  <span className="font-medium">{getCountryLabel(country)}</span>
                </Row>

                <div className="grid grid-cols-auto-250 gap-4">
                  {costs.map(({ name, type, value, currency }, index) => (
                    <VariableCostInput
                      key={`reprice-${project.id}--${id}--${country}--${name}`}
                      label={name}
                      type={type}
                      name={`${country}#${id}#value#${index}`}
                      currency={currency}
                      currencyDisabled
                      onChange={changeValues}
                      value={value}
                    />
                  ))}
                </div>

                <p className="rounded-lg bg-muted p-3">{justification}</p>
              </Column>
            ))}

            {!!outsourceConfig && (
              <Column className="relative rounded-lg border p-4" gap="6">
                <Row>
                  <Label>{t('provide-name')}</Label>

                  <span className="font-semibold">{outsourceConfig?.providerName}</span>
                </Row>

                <Column gap="2">
                  <Label>{t('provide-cost')}</Label>

                  <MonetaryInput
                    name={`service-outsource#${id}`}
                    disabled={outsourceConfig?.providerCost?.confirmed}
                    currency={outsourceConfig?.providerCost?.currency || ''}
                    currencyDisabled
                    value={outsourceConfig?.providerCost?.value || ''}
                    onChange={
                      outsourceConfig?.providerCost?.confirmed
                        ? undefined
                        : handleProviderCostChange
                    }
                  />
                </Column>

                <Column gap="2">
                  <Label>{t('cost-to-client')}</Label>

                  <MonetaryInput
                    disabled
                    currency={outsourceConfig?.clientCost?.currency || ''}
                    currencyDisabled
                    value={outsourceConfig?.clientCost?.value || ''}
                  />

                  <span className="text-sm italic">{t('this-is-the-entire-service-cost')}</span>
                </Column>

                <Alert level="warning" title={t('provider-cost-is-total')}>
                  <Trans i18nKey={'an-outsource-service-cost-desc'} />
                </Alert>

                {!!outsourceConfig?.providerCost?.confirmed && (
                  <Alert title={t('provider-cost-has-been-confirmed')}>
                    {t('an-outsource-invoice-is-currently-confirmed')}
                  </Alert>
                )}
              </Column>
            )}
          </Column>
        ))}
      </Column>

      <hr />

      {comment && (
        <Column gap="2">
          <Label>{t('user-general-comment')}</Label>

          <p className="rounded-lg bg-muted p-3">{comment}</p>
        </Column>
      )}

      <AttachedFiles label={<Label>{t('files')}</Label>} files={files} />

      <Column gap="6">
        <LabeledTextarea label={t('add-any-comments') as string} ref={commentRef} />

        <PriceChange
          currency={project.currency}
          currentServices={currentServices}
          repricedServices={control}
        />

        <FullyInvoicedRepriceAlert invoice={invoice} />

        <ConfirmRepriceButton
          onClick={updatePrices}
          disabled={!canSubmit}
          loading={answerReprice.loading}
          currentServices={currentServices}
          repricedServices={control}
          currency={project.currency}
        />
      </Column>
    </Column>
  );
}
